import { useEffect } from 'react'
import {
  FormLabel,
  Select,
  Switch,
  HStack,
  VStack,
  Text,
  Icon,
  Box,
  Tooltip,
} from '@chakra-ui/react'
import { Clock, Flame, Pencil } from 'lucide-react'
import {
  EXPIRY_OPTIONS,
  tierMeetsMin,
  isExpiryOptionAllowed,
  enforceSecurityTierRules,
  isBurnDisabled as checkBurnDisabled,
  isModifiableDisabled as checkModifiableDisabled,
  getBurnTooltip,
  getModifiableTooltip,
  getTierMaxExpiryLabel,
} from '@/lib/account/tier-permissions'
import type { SecurityOptions } from '@/types/share'

interface SecurityConfigProps {
  options: SecurityOptions
  onChange: (options: SecurityOptions) => void
  isPublic?: boolean
  tier?: 'free' | 'starter' | 'pro'
}

export function SecurityConfig({ options, onChange, isPublic = false, tier = 'free' }: SecurityConfigProps) {
  const isPro = tier === 'pro'

  // Enforce tier limits on security options
  useEffect(() => {
    const enforced = enforceSecurityTierRules(options, tier)
    if (enforced) onChange(enforced)
  }, [tier, options.expiresIn, isPro]) // eslint-disable-line react-hooks/exhaustive-deps

  const burnDisabled = checkBurnDisabled(isPublic, options.isModifiable)
  const modifiableDisabled = checkModifiableDisabled(tier, isPublic, options.burnAfterRead)
  const maxExpiryLabel = getTierMaxExpiryLabel(tier)

  return (
    <VStack spacing={4} align="stretch">
      <HStack justify="space-between">
        <HStack spacing={2}>
          <Icon as={Clock} boxSize={4} color="stone.500" />
          <Box>
            <FormLabel mb={0}>Expires after</FormLabel>
            {maxExpiryLabel && (
              <Text fontSize="xs" color="stone.500">
                {maxExpiryLabel}
              </Text>
            )}
          </Box>
        </HStack>
        <Select
          value={options.expiresIn}
          onChange={(e) => {
            const selectedValue = e.target.value
            if (isExpiryOptionAllowed(selectedValue, tier)) {
              onChange({ ...options, expiresIn: selectedValue })
            }
          }}
          w="auto"
          size="sm"
        >
          {EXPIRY_OPTIONS.map((option) => {
            const isDisabled = 'minTier' in option ? !tierMeetsMin(tier, option.minTier) : false
            return (
              <option
                key={option.value}
                value={option.value}
                disabled={isDisabled}
              >
                {option.label}
              </option>
            )
          })}
        </Select>
      </HStack>

      <Tooltip
        label={getBurnTooltip(isPublic, options.isModifiable)}
        isDisabled={!burnDisabled}
        hasArrow
        placement="top"
      >
        <HStack justify="space-between" opacity={burnDisabled ? 0.5 : 1}>
          <HStack spacing={2} flex={1}>
            <Icon as={Flame} boxSize={4} color="stone.500" />
            <Box>
              <FormLabel mb={0}>Burn after read</FormLabel>
              <Text fontSize="xs" color="stone.500">
                Share is deleted after first view
              </Text>
            </Box>
          </HStack>
          <Switch
            isChecked={options.burnAfterRead}
            isDisabled={burnDisabled}
            onChange={(e) =>
              onChange({ ...options, burnAfterRead: e.target.checked })
            }
          />
        </HStack>
      </Tooltip>

      <Tooltip
        label={getModifiableTooltip(tier, isPublic, options.burnAfterRead)}
        isDisabled={!modifiableDisabled}
        hasArrow
        placement="top"
      >
        <HStack justify="space-between" opacity={modifiableDisabled ? 0.5 : 1}>
          <HStack spacing={2} flex={1}>
            <Icon as={Pencil} boxSize={4} color="stone.500" />
            <Box>
              <FormLabel mb={0}>Allow modifications {!isPro && '(Pro)'}</FormLabel>
              <Text fontSize="xs" color="stone.500">
                You can update the content later
              </Text>
            </Box>
          </HStack>
          <Switch
            isChecked={options.isModifiable}
            isDisabled={modifiableDisabled}
            onChange={(e) =>
              onChange({ ...options, isModifiable: e.target.checked })
            }
          />
        </HStack>
      </Tooltip>
    </VStack>
  )
}
